import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Crown, 
  Rocket, 
  Briefcase, 
  TrendingUp, 
  ChevronRight, 
  Check,
  Sparkles,
  Eye,
  EyeOff
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export interface StatusCircle {
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

export const STATUS_CIRCLES: StatusCircle[] = [
  {
    value: "established",
    label: "Established",
    description: "Business owners, executives, senior professionals",
    icon: <Crown className="w-5 h-5" />,
    color: "from-amber-500 to-amber-600",
  },
  {
    value: "builder",
    label: "Builder",
    description: "Entrepreneurs, founders, high-growth careers",
    icon: <Rocket className="w-5 h-5" />,
    color: "from-purple-500 to-purple-600",
  },
  {
    value: "professional",
    label: "Professional",
    description: "Stable careers, skilled professionals",
    icon: <Briefcase className="w-5 h-5" />,
    color: "from-blue-500 to-blue-600",
  },
  {
    value: "growth",
    label: "Growth-Minded",
    description: "Students, creatives, early-stage builders",
    icon: <TrendingUp className="w-5 h-5" />,
    color: "from-emerald-500 to-emerald-600",
  },
];

interface StatusCircleSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCircle: string;
  onSelect: (circle: string) => void;
  showOnProfile: boolean;
  onToggleVisibility: (show: boolean) => void;
}

const StatusCircleSelector = ({
  isOpen,
  onClose,
  selectedCircle,
  onSelect,
  showOnProfile,
  onToggleVisibility,
}: StatusCircleSelectorProps) => {
  const { user, refreshProfile } = useAuthContext();
  const [localSelected, setLocalSelected] = useState(selectedCircle);
  const [localShowOnProfile, setLocalShowOnProfile] = useState(showOnProfile);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state when props change
  useEffect(() => {
    setLocalSelected(selectedCircle);
    setLocalShowOnProfile(showOnProfile);
  }, [selectedCircle, showOnProfile, isOpen]);

  const handleApply = async () => {
    // Update filter state
    onSelect(localSelected);
    onToggleVisibility(localShowOnProfile);

    // Save to database if user is logged in
    if (user) {
      setIsSaving(true);
      const { error } = await supabase
        .from("profiles")
        .update({
          social_status: localSelected === "any" ? null : localSelected,
          status_visibility: localShowOnProfile ? "public" : "private",
        })
        .eq("id", user.id);

      if (error) {
        toast({
          title: "Error",
          description: "Failed to save preferences",
          variant: "destructive",
        });
      } else {
        refreshProfile();
        toast({
          title: "Preferences saved",
          description: "Your status preferences have been updated",
        });
      }
      setIsSaving(false);
    }

    onClose();
  };

  const handleCircleClick = (value: string) => {
    // Toggle selection - if already selected, deselect to "any"
    setLocalSelected(localSelected === value ? "any" : value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md mx-auto max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-b from-background to-muted/20">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-5 h-5 text-primary" />
            Status & Lifestyle
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Match with people at a similar life stage, ambition level, and pace.
          </p>
          <p className="text-xs text-muted-foreground/80">
            Participation is optional and private by default.
          </p>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh]">
          <div className="p-4 space-y-3">
            {/* Any / All circles option */}
            <button
              onClick={() => setLocalSelected("any")}
              className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                localSelected === "any"
                  ? "border-primary bg-primary/5 shadow-md"
                  : "border-border hover:border-primary/30 hover:bg-muted/50"
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-2.5 rounded-xl bg-gradient-to-br from-gray-400 to-gray-500 text-white`}>
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-foreground">All Circles</h3>
                  <p className="text-sm text-muted-foreground">Show everyone, no filter applied</p>
                </div>
              </div>
              {localSelected === "any" && (
                <div className="p-1 rounded-full bg-primary text-primary-foreground">
                  <Check className="w-4 h-4" />
                </div>
              )}
            </button>

            {/* Status circle cards */}
            {STATUS_CIRCLES.map((circle) => (
              <button
                key={circle.value}
                onClick={() => handleCircleClick(circle.value)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                  localSelected === circle.value
                    ? "border-primary bg-primary/5 shadow-md"
                    : "border-border hover:border-primary/30 hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${circle.color} text-white shadow-lg`}>
                    {circle.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-foreground">{circle.label}</h3>
                    <p className="text-sm text-muted-foreground">{circle.description}</p>
                  </div>
                </div>
                {localSelected === circle.value ? (
                  <div className="p-1 rounded-full bg-primary text-primary-foreground">
                    <Check className="w-4 h-4" />
                  </div>
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
            ))}
          </div>
        </ScrollArea>

        {/* Visibility toggle */}
        <div className="px-6 py-4 border-t bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {localShowOnProfile ? (
                <Eye className="w-5 h-5 text-primary" />
              ) : (
                <EyeOff className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <Label htmlFor="show-circle" className="text-sm font-medium cursor-pointer">
                  Show my circle on profile
                </Label>
                <p className="text-xs text-muted-foreground">
                  {localShowOnProfile ? "Others can see your status" : "Your status stays private"}
                </p>
              </div>
            </div>
            <Switch
              id="show-circle"
              checked={localShowOnProfile}
              onCheckedChange={setLocalShowOnProfile}
            />
          </div>
        </div>

        {/* Apply button */}
        <div className="px-6 pb-6 pt-2">
          <Button
            onClick={handleApply}
            className="w-full pop-gradient"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Explore Circles"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatusCircleSelector;
